import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import BlankTractor from '../../../assets/images/black-tractor.jpg';
import { MainHead } from '../../../components/common/head/MainHead';
import { Icon } from '../../../components/common/icon/Icon';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { useGetMachinesQuery } from '../../../hooks/api/machines/queries/useGetMachines.query';
import { useLayout } from '../../../hooks/screen/useLayout';
import { DictionaryChooseTagSection } from '../components/DictionaryChooseTagSection';

export const DictionaryMachineListPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const tagId = useMemo<string | null>(() => {
    if (params.has('tagId')) {
      return params.get('tagId');
    }
    return null;
  }, [params]);

  const searchTerm = useMemo<string | null>(() => {
    if (params.has('searchTerm')) {
      return params.get('searchTerm');
    }
    return null;
  }, [params]);

  const from = useMemo<string | null>(() => {
    if (params.has('from')) {
      return params.get('from');
    }
    return null;
  }, [params]);

  const searchParam = useMemo<{ tagId?: string; searchTerm?: string }>(() => {
    if (tagId) {
      return { tagId };
    }

    if (searchTerm) {
      return { searchTerm };
    }

    return {};
  }, [tagId, searchTerm]);

  const { data: dictionaryMachines, isLoading: isGetDictionaryMachinesQueryLoading } = useGetMachinesQuery({
    searchParam,
  });

  useLayout({
    fixedHeader: (
      <>
        <MainHead backButton={{ isShow: true }} />
        <DictionaryChooseTagSection />
      </>
    ),
    fixedHeaderHeight: 100,
    fixedFooterHeight: 92,
  });

  if (isGetDictionaryMachinesQueryLoading) return <AnimateLoading />;

  if (dictionaryMachines && dictionaryMachines.length > 0) {
    return (
      <Container>
        <MachineListSection>
          {dictionaryMachines.map((machine) => (
            <MachineItem onClick={() => navigate(`/dictionary/machine/${machine.id}`)}>
              <MachineLeftSection>
                <MachineImage src={machine.imageMain ? machine.imageMain : BlankTractor} />
                <MachineInfo>
                  <MachineBrand>{machine.brand?.name}</MachineBrand>
                  <MachineTitle>{`${machine.model}, ${machine.engineOutputInPs}마력`}</MachineTitle>
                  <MachineSize>
                    {`길이 ${machine.width.toLocaleString()}mm`}
                    <Icon name={'VerticalLine'} width={2} height={12} />
                    {`넓이 ${machine.width.toLocaleString()}mm`}
                    <Icon name={'VerticalLine'} width={2} height={12} />
                    {`높이 ${machine.height.toLocaleString()}mm`}
                  </MachineSize>
                </MachineInfo>
              </MachineLeftSection>
              <GoToMachineDefailtButton>
                <Icon name={'ArrowRight'} width={12} height={12} />
              </GoToMachineDefailtButton>
            </MachineItem>
          ))}
        </MachineListSection>
      </Container>
    );
  }

  return <></>;
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
`;

const MachineListSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  padding: 16px;

  gap: 16px;
`;

const MachineItem = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;

  cursor: pointer;
`;

const MachineLeftSection = styled.div`
  width: 100%;

  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 16px;
`;

const MachineImage = styled.img`
  width: 64px;
  height: 64px;

  border-radius: 16px;

  background-color: #f3f4f5;
`;

const MachineInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  gap: 4px;
`;

const MachineBrand = styled.span`
  color: #445eff;
  font-size: 12px;
  line-height: 100%; /* 12px */
`;

const MachineTitle = styled.span`
  color: #232429;
  font-size: 14px;
  font-weight: 500;
  line-height: 100%; /* 14px */
`;

const MachineSize = styled.span`
  display: flex;

  gap: 4px;

  color: #898b94;
  font-size: 12px;
  line-height: 100%; /* 12px */

  white-space: nowrap;
`;

const GoToMachineDefailtButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
`;
